import { useFetchGet, useFetchPost } from '@/composables/useFetch'
import { CoreType } from '@/interfaces/core.interface'
import { ClickedEntityType, useSelectionStore } from '@/pinia/selection'
import { formatNumber } from '@/utils/math'
import { getRealmId } from '@/utils/realmId'
import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const countToCoreType = {
  complexes: 'COMPLEX',
  realEstates: 'REAL_ESTATE',
  constructions: 'CONSTRUCTION',
  levels: 'LEVEL',
  spaces: 'SPACE'
}

const coreTypeToCount = {
  COMPLEX: 'complexes',
  REAL_ESTATE: 'realEstates',
  CONSTRUCTION: 'constructions',
  LEVEL: 'levels',
  SPACE: 'spaces'
}

const levelToEntityTypes: Partial<Record<ClickedEntityType, ClickedEntityType[]>> = {
  REGION: ['COMPLEX', 'CONSTRUCTION', 'SPACE'],
  COMPLEX: ['REAL_ESTATE', 'CONSTRUCTION', 'SPACE'],
  REAL_ESTATE: ['CONSTRUCTION', 'LEVEL', 'SPACE'],
  CONSTRUCTION: ['LEVEL', 'SPACE'],
  LEVEL: ['SPACE']
}

const titles: Partial<Record<ClickedEntityType, string[]>> = {
  COMPLEX: ['general__title__complex', 'general__data__complexes'],
  REAL_ESTATE: ['general__title__realestate', 'general__data__realestates'],
  CONSTRUCTION: ['general__text__building', 'general__data__buildings'],
  LEVEL: ['general__text__floor', 'general__text__floors'],
  SPACE: ['general__data__room', 'general__data__rooms']
}

const titleRealmOverrides: Record<string, Partial<Record<ClickedEntityType, string[]>>> = {
  akademiskahus: {
    COMPLEX: ['title__complex__campus', 'title__complex__campuses']
  },
  sam: {
    COMPLEX: ['title__complex__member', 'title__complex__members']
  },
  'newsec-online': {
    COMPLEX: ['title__complex__customer', 'title__complex__customers']
  }
}

type Counts = {
  id: string | null
  displayName: string | null
  labelTenant: string | null
  nameTenant: string | null
  name: string | null
  complexes: number
  realEstates: number
  constructions: number
  levels: number
  spaces: number
}

interface ReportRespone {
  count: Counts
  displayName: string
  id: string
  labelTenant: string | null
  measurements: Record<string, number>
  name: string
  nameTenant: string | null
  type: CoreType
  subGroupReports: ReportRespone[] | null
  subGroupCount: Counts[] | null
}

export interface Measurement {
  title: string
  value: string
  unit: string
  info: string
}

export interface LineSummary {
  categoryId: string
  categoryName: string
  count: number
  types: {
    typeName: string
    count: number
    color: string
  }[]
}

export interface ZoneSummary {
  id: string
  categoryName: string
  count: number
  area: string
  types: {
    typeName: string
    count: number
    area: string
    color: string
  }[]
}

interface PoiQuery {
  coreIds: string[]
}

export interface PoiSummary {
  id: string
  categoryName: string
  count: number
  types: {
    typeName: string
    count: number
    color: string
  }[]
}

interface Report {
  summaryReport: {
    itemTitle: string
    itemType: CoreType
    itemValue: string
    unit: string
  }[]
  groupData: {
    id: string
    value1?: string
    value2?: string
    value3?: string
    value4?: string
  }[]
  groupTitles: {
    heading1?: string
    heading1extra?: string
    heading2?: string
    heading3?: string
  }
  measurements: Measurement[]
  lineSummaryData?: LineSummary[]
  zoneSummaryData?: ZoneSummary[]
  poiSummaryData?: PoiSummary[]
}

function getIdsRecursive(report: ReportRespone): string[] {
  const subGroupIds = report.subGroupReports?.flatMap(getIdsRecursive) ?? []
  return [report.id, ...subGroupIds].filter(Boolean)
}

export function getTitleKey(entityType: ClickedEntityType, plural: boolean, realm: string): string {
  const [singular, pluralTitle] = titleRealmOverrides[realm]?.[entityType] ??
    titles[entityType] ?? ['', '']
  return plural ? pluralTitle : singular
}

const coreEntityTypes = ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION', 'LEVEL', 'SPACE']

export const useReport = defineStore('report', () => {
  const selectionStore = useSelectionStore()
  const { clickedEntityType, currentEntity } = storeToRefs(selectionStore)
  const { t } = useI18n()
  const realm = getRealmId()

  const { data, isLoading: reportIsLoading } = useFetchGet<ReportRespone>(
    computed(() => {
      return currentEntity.value?.id !== undefined &&
        coreEntityTypes.includes(clickedEntityType.value)
        ? `core/v2/reports/${currentEntity.value.id}`
        : clickedEntityType.value === 'REGION'
        ? 'core/v2/reports'
        : undefined
    })
  )

  const hasSpaces = computed(
    () =>
      data.value?.count[coreTypeToCount.SPACE] !== undefined &&
      data.value.count[coreTypeToCount.SPACE] > 0
  )

  const childIds = computed(() => {
    return data.value ? getIdsRecursive(data.value) : undefined
  })

  const { data: lineSummary, isLoading: lineSummaryIsLoading } = useFetchGet<LineSummary[]>(
    computed(() =>
      currentEntity.value?.id !== undefined
        ? `/zone/v1/lines/summary/${currentEntity.value.id}`
        : undefined
    )
  )

  const { data: zoneSummary, isLoading: zoneSummaryIsLoading } = useFetchGet<ZoneSummary[]>(
    computed(() =>
      currentEntity.value?.id !== undefined
        ? `/zone/v1/zones/summary/${currentEntity.value.id}`
        : undefined
    ),
    (data) => {
      return data?.map((d) => ({
        ...d,
        types: d.types.map((t) => ({
          ...t,
          area: formatNumber(parseFloat(t.area), 0)
        })),
        area: formatNumber(parseFloat(d.area), 0)
      }))
    }
  )

  const { data: poiSummary, isLoading: poiSummaryIsLoading } = useFetchPost<PoiQuery, PoiSummary[]>(
    computed(() => (currentEntity.value?.id !== undefined ? `/poi/v2/pois/summary` : undefined)),
    computed(() =>
      childIds.value && childIds.value.length > 0 ? { coreIds: childIds.value } : null
    )
  )

  const isLoading = computed(() => {
    return (
      reportIsLoading.value ||
      lineSummaryIsLoading.value ||
      zoneSummaryIsLoading.value ||
      poiSummaryIsLoading.value
    )
  })

  const getTitle = (entityType: ClickedEntityType, plural: boolean) =>
    t(getTitleKey(entityType, plural, realm ?? ''))

  const groupTitles = computed<Report['groupTitles']>(() => {
    const extraTitle =
      clickedEntityType.value === 'LEVEL' ? { heading1extra: t('general__text__tenant-name') } : {}
    const entityTypes = levelToEntityTypes[clickedEntityType.value] || []

    const headings = entityTypes.reduce((acc, entityType, i) => {
      const isPlural = i > 0 || entityTypes.length === 1
      acc[`heading${i + 1}`] = getTitle(entityType, isPlural)
      return acc
    }, {})

    return { ...extraTitle, ...headings }
  })

  const groupData = computed<Report['groupData']>(() => {
    return (
      (
        (data.value?.subGroupCount?.length
          ? data.value?.subGroupCount?.map((r) => ({ ...r, count: r }))
          : undefined) ?? data.value?.subGroupReports
      )?.map((r) => ({
        id: r.id,
        value1: r.displayName,
        value1extra: r.nameTenant ?? undefined,
        ...(levelToEntityTypes[clickedEntityType.value] ?? [])
          .slice(1)
          .map((type) => r.count[coreTypeToCount[type]] ?? '0')
          .reduce((acc, value, i) => {
            acc[`value${i + 2}`] = value.toString()
            return acc
          }, {})
      })) ?? []
    ).toSorted((a, b) => (a.value1 as string).localeCompare(b.value1 as string))
  })

  const report = computed<Report>(
    (): Report => ({
      summaryReport: data.value?.count
        ? Object.entries(data.value.count)
            .filter(([, value]) => value !== null && typeof value === 'number')
            .map(([key, value]) => ({
              itemTitle: getTitle(countToCoreType[key], (value as number) > 1),
              itemType: countToCoreType[key],
              itemValue: (value as number).toString(),
              unit: 'pieces'
            }))
        : [],
      groupData: groupData.value,
      groupTitles: groupTitles.value,
      measurements: data.value?.measurements
        ? Object.entries(data.value.measurements).map(([key, value]) => ({
            title: t(`general__data__area-${key.toLowerCase()}`),
            value: formatNumber(value, 0),
            unit: 'm²',
            info: ''
          }))
        : [],
      lineSummaryData: lineSummary.value,
      zoneSummaryData: zoneSummary.value,
      poiSummaryData: poiSummary.value
    })
  )

  return {
    report,
    hasSpaces,
    isLoading
  }
})
